import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppImage from '../../components/AppImage';
import Button from '../../components/ui/Button';

const LandingPage = () => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentStatementIndex, setCurrentStatementIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  // Curated luxury furniture images from Unsplash
  const images = [
    'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1920&q=80', // Modern luxury living room
    'https://images.unsplash.com/photo-1540518614846-7eded433c457?w=1920&q=80', // Elegant bedroom
    'https://images.unsplash.com/photo-1567016432779-094069958ea5?w=1920&q=80', // Contemporary furniture
    'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1920&q=80', // Luxury interior
    'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=1920&q=80', // Modern living space
    'https://images.unsplash.com/photo-1615529328331-f8917597711f?w=1920&q=80', // Designer furniture
    'https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=1920&q=80', // Luxury home
    'https://images.unsplash.com/photo-1615874959474-d609969a20ed?w=1920&q=80', // Premium interior
    'https://images.unsplash.com/photo-1616137466211-f939a420be84?w=1920&q=80', // Modern design
    'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=1920&q=80', // Luxury sofa
    'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?w=1920&q=80', // Designer chair
    'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?w=1920&q=80', // Modern interior
  ];

  // Typography statements - Consistent Helvetica styling
  const statements = [
    {
      text: "You furnish your spaces with quality...",
      size: "text-5xl md:text-7xl lg:text-8xl",
      weight: "font-bold",
    },
    {
      text: "We're your logistics arm for that mission.",
      size: "text-5xl md:text-7xl lg:text-8xl",
      weight: "font-bold",
    },
  ];

  // Auto-advance slideshow every 6 seconds (20% slower)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [images.length]);

  // Typewriter effect
  useEffect(() => {
    const currentStatement = statements[currentStatementIndex].text;
    let currentIndex = 0;

    setDisplayedText('');
    setIsTyping(true);

    const typingInterval = setInterval(() => {
      if (currentIndex <= currentStatement.length) {
        setDisplayedText(currentStatement.slice(0, currentIndex));
        currentIndex++;
      } else {
        setIsTyping(false);
        clearInterval(typingInterval);
      }
    }, 60); // 60ms per character (20% slower)

    return () => clearInterval(typingInterval);
  }, [currentStatementIndex]);

  // Rotate to next statement after typing completes and pause
  useEffect(() => {
    if (!isTyping) {
      const timeout = setTimeout(() => {
        setCurrentStatementIndex((prevIndex) => (prevIndex + 1) % statements.length);
      }, 3600); // 3.6 second pause after typing completes (20% slower)

      return () => clearTimeout(timeout);
    }
  }, [isTyping, statements.length]);

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Image Slideshow with Crossfade */}
      <div className="absolute inset-0">
        {images.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
          >
            <img
              src={image}
              alt={`Luxury furniture ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>

      {/* Dark overlay for text contrast */}
      <div className="absolute inset-0 bg-black/30" />

      {/* Content Container */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* White Masthead */}
        <header className="bg-white/95 backdrop-blur-sm shadow-sm">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
            {/* Logo - Left Aligned */}
            <div className="flex items-center">
              <AppImage
                src="/assets/images/LaSea_Group_logo-1765111940679.png"
                alt="LaSea Group Logo"
                className="h-12 w-auto object-contain"
              />
            </div>

            {/* Client Login Button - Right Aligned */}
            <Button
              onClick={() => navigate('/client-login')}
              className="bg-primary hover:bg-primary/90 text-white px-8 py-2.5 rounded-lg font-medium transition-all duration-200 hover:shadow-lg"
            >
              Client Login
            </Button>
          </div>
        </header>

        {/* Central Typography - Typewriter Effect */}
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h1
              className={`font-sans ${statements[currentStatementIndex].size} ${statements[currentStatementIndex].weight} text-white leading-tight tracking-tight`}
            >
              {displayedText}
              <span className="animate-pulse">|</span>
            </h1>
          </div>
        </div>

        {/* Admin Button - Bottom */}
        <div className="pb-8 flex justify-center">
          <button
            onClick={() => navigate('/admin-login')}
            className="text-white/90 hover:text-white text-sm font-light tracking-wide transition-colors duration-200 underline underline-offset-4 decoration-white/50 hover:decoration-white"
          >
            Admin
          </button>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;